import { ArrowRight } from "lucide-react";
import { COMPANY, IMAGES } from "../../lib/constants";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[88vh] flex flex-col justify-end overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Aircraft inside maintenance hangar"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1D3A]/95 via-[#0B1D3A]/85 to-[#0B1D3A]/95" />
        {/* Faint technical grid */}
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        {/* Aviation decorative overlay — radar, schematic engine, runway markings */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 1920 1080"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true"
        >
          {/* Radar sweep (top-right) */}
          <g transform="translate(1620 230)" stroke="#C9A961" fill="none" opacity="0.35">
            <circle cx="0" cy="0" r="60" strokeWidth="1" />
            <circle cx="0" cy="0" r="120" strokeWidth="1" opacity="0.5" />
            <circle cx="0" cy="0" r="180" strokeWidth="0.5" opacity="0.3" />
            <line x1="-200" y1="0" x2="200" y2="0" strokeWidth="0.5" strokeDasharray="4 6" opacity="0.5" />
            <line x1="0" y1="-200" x2="0" y2="200" strokeWidth="0.5" strokeDasharray="4 6" opacity="0.5" />
            <path d="M 0 0 L 165 -75 A 180 180 0 0 0 180 0 Z" fill="#C9A961" opacity="0.18" />
            <text x="68" y="-5" fontSize="9" fill="#C9A961" fontFamily="JetBrains Mono, monospace" opacity="0.7">
              N28°47′12″
            </text>
            <text x="68" y="14" fontSize="9" fill="#C9A961" fontFamily="JetBrains Mono, monospace" opacity="0.7">
              W80°23′07″
            </text>
          </g>

          {/* Engine schematic (bottom-right) */}
          <g
            transform="translate(1520 760)"
            stroke="#FFFFFF"
            fill="none"
            opacity="0.18"
            strokeWidth="1"
          >
            {/* outer engine nacelle */}
            <ellipse cx="0" cy="0" rx="180" ry="78" />
            <ellipse cx="0" cy="0" rx="180" ry="78" strokeDasharray="3 4" opacity="0.5" />
            {/* fan blades */}
            <circle cx="-130" cy="0" r="58" />
            <g transform="translate(-130 0)">
              {[0, 30, 60, 90, 120, 150].map((deg) => (
                <line
                  key={deg}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="-56"
                  transform={`rotate(${deg})`}
                  strokeWidth="0.8"
                />
              ))}
              <circle cx="0" cy="0" r="12" />
            </g>
            {/* core */}
            <line x1="-72" y1="-32" x2="170" y2="-32" />
            <line x1="-72" y1="32" x2="170" y2="32" />
            <line x1="-72" y1="-58" x2="170" y2="-58" strokeDasharray="2 4" />
            <line x1="-72" y1="58" x2="170" y2="58" strokeDasharray="2 4" />
            {/* exhaust */}
            <path d="M 170 -32 L 210 -16 L 210 16 L 170 32 Z" />
            {/* dimension callouts */}
            <line x1="-180" y1="-95" x2="180" y2="-95" strokeWidth="0.5" />
            <line x1="-180" y1="-98" x2="-180" y2="-92" strokeWidth="0.5" />
            <line x1="180" y1="-98" x2="180" y2="-92" strokeWidth="0.5" />
            <text x="-25" y="-100" fontSize="10" fill="#FFFFFF" fontFamily="JetBrains Mono, monospace" opacity="0.7">
              CFM56
            </text>
          </g>

          {/* Runway centerline markings (left edge, vertical) */}
          <g stroke="#C9A961" strokeWidth="3" opacity="0.25">
            <line x1="38" y1="240" x2="38" y2="290" />
            <line x1="38" y1="320" x2="38" y2="370" />
            <line x1="38" y1="400" x2="38" y2="450" />
            <line x1="38" y1="480" x2="38" y2="530" />
            <line x1="38" y1="560" x2="38" y2="610" />
            <line x1="38" y1="640" x2="38" y2="690" />
          </g>
          <text
            x="50"
            y="218"
            fontSize="11"
            fill="#C9A961"
            fontFamily="JetBrains Mono, monospace"
            opacity="0.45"
            letterSpacing="2"
          >
            RWY 09 / 27
          </text>

          {/* Heading band (top-left) */}
          <g transform="translate(60 70)" opacity="0.35">
            <text x="0" y="0" fontSize="10" fill="#C9A961" fontFamily="JetBrains Mono, monospace" letterSpacing="2">
              HDG 090° · ALT FL350 · IAS 480kt
            </text>
            <line x1="0" y1="8" x2="220" y2="8" stroke="#C9A961" strokeWidth="0.5" />
          </g>

          {/* Aircraft top-down silhouette (mid-right, subtle) */}
          <g
            transform="translate(1700 520) rotate(35)"
            fill="#FFFFFF"
            opacity="0.07"
          >
            <path d="M 0 -80 L 6 -60 L 6 -20 L 70 0 L 70 12 L 6 4 L 6 40 L 30 56 L 30 64 L 0 58 L -30 64 L -30 56 L -6 40 L -6 4 L -70 12 L -70 0 L -6 -20 L -6 -60 Z" />
          </g>

          {/* Tail registration tag (bottom-left corner) */}
          <g transform="translate(60 1000)" opacity="0.4">
            <rect x="0" y="0" width="190" height="34" fill="none" stroke="#C9A961" strokeWidth="0.8" />
            <text
              x="14"
              y="22"
              fontSize="13"
              fill="#C9A961"
              fontFamily="JetBrains Mono, monospace"
              letterSpacing="3"
            >
              N · P · S — KMIA
            </text>
          </g>

          {/* Subtle contrail streaks (diagonal across upper area) */}
          <g stroke="#FFFFFF" fill="none" opacity="0.08" strokeWidth="1">
            <path d="M -100 180 Q 600 140 1400 240" strokeDasharray="2 8" />
            <path d="M -100 320 Q 800 270 1600 380" strokeDasharray="1 10" opacity="0.6" />
          </g>
        </svg>
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 w-full pb-24 pt-48 lg:pt-56">
        <div className="max-w-5xl nps-fade-up">
          <p className="nps-eyebrow text-[#C9A961] mb-6">
            Engine Sales · Engine Piece Part Sales
          </p>

          <h1
            data-testid="hero-company-name"
            className="font-black leading-[0.92] tracking-tight text-6xl sm:text-7xl lg:text-[9rem]"
            style={{
              fontFamily: "'Big Shoulders Display', 'Manrope', sans-serif",
              backgroundImage:
                "linear-gradient(135deg, #F4D77A 0%, #C9A961 38%, #E8C77A 55%, #8E6E26 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              textShadow: "0 2px 30px rgba(201,169,97,0.25)",
              letterSpacing: "0",
              textTransform: "uppercase",
            }}
          >
            Noble Power
            <br />
            <span
              className="text-white"
              style={{
                fontFamily: "'Big Shoulders Display', 'Manrope', sans-serif",
                backgroundImage: "none",
                WebkitTextFillColor: "white",
              }}
            >
              Solutions
            </span>
          </h1>

          <p className="mt-7 font-display text-xl sm:text-2xl lg:text-3xl text-white/90 tracking-tight font-light italic">
            {COMPANY.tagline}
          </p>

          <p className="mt-7 text-lg text-white/70 max-w-2xl leading-relaxed font-light">
            A trusted partner for Engine Sales, Engine Piece Parts, and MRO
            Solutions across Boeing, Airbus, and McDonnell Douglas fleets.
          </p>

          <div className="mt-10">
            <a
              href="#contact"
              data-testid="hero-rfq-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#C9A961] hover:bg-[#B0904A] text-[#0B1D3A] px-8 py-4 font-semibold tracking-wide rounded-sm transition-colors"
            >
              Request a Quote
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Bottom marquee */}
      <div className="relative bg-[#0B1D3A] border-t border-[#C9A961]/20 overflow-hidden">
        <div className="flex whitespace-nowrap nps-marquee-track py-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-12 px-8 text-white/55 text-xs uppercase tracking-[0.22em] font-medium font-mono"
            >
              <span>Boeing 737 Classic / NG / MAX</span>
              <span className="text-[#C9A961]">◆</span>
              <span>Boeing 757-200 / 300</span>
              <span className="text-[#C9A961]">◆</span>
              <span>Airbus A300 Family</span>
              <span className="text-[#C9A961]">◆</span>
              <span>McDonnell Douglas MD-80 Series</span>
              <span className="text-[#C9A961]">◆</span>
              <span>Engine Piece Parts · MRO Sales</span>
              <span className="text-[#C9A961]">◆</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
